import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const KEY = "reddy_notif_permission_prompt_v4";

/**
 * "Allow Notifications" popup — controlled by admin setting `notification_popup_enabled`.
 * Only shows when:
 *   - Admin has toggled ON in admin panel
 *   - User is logged in
 *   - Browser/WebView notification permission is NOT 'granted'
 *   - User hasn't dismissed it in this session
 * Admin toggles OFF → popup disappears for everyone.
 */
export default function AllowNotificationsPopup() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") return;
    if (sessionStorage.getItem(KEY) === "1") return;
    // Check admin setting before showing
    api.get("/settings")
      .then(({ data }) => {
        if (data?.notification_popup_enabled === true) {
          setTimeout(() => setOpen(true), 800);
        }
      })
      .catch(() => {});
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const allow = async () => {
    try {
      const res = await Notification.requestPermission();
      if (res === "granted") {
        // Also call OneSignal native prompt if available (Android WebView bridge)
        try { window.AndroidBridge && window.AndroidBridge.promptForPush && window.AndroidBridge.promptForPush(); } catch (_) {}
        dismiss();
      } else {
        // If denied, guide user to phone settings
        const pkg = "in.rajakhaiwal.app";
        try {
          window.location.href = `intent://#Intent;action=android.settings.APP_NOTIFICATION_SETTINGS;S.android.provider.extra.APP_PACKAGE=${pkg};end`;
        } catch (_) {}
        setTimeout(dismiss, 1500);
      }
    } catch (_) {
      dismiss();
    }
  };

  if (!open || !user) return null;

  return (
    <div
      data-testid="allow-notif-overlay"
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
      style={{ animation: "fadeIn 300ms ease" }}
    >
      <div
        data-testid="allow-notif-popup"
        className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: "linear-gradient(160deg, #1a0a0a 0%, #2a0f14 45%, #3d0f0a 100%)",
          border: "1px solid rgba(239, 68, 68, 0.35)",
          boxShadow: "0 25px 70px -12px rgba(239, 68, 68, 0.5), 0 0 0 1px rgba(255,255,255,0.06) inset",
          animation: "popIn 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {/* Glow blobs */}
        <div className="absolute -top-24 -right-16 w-64 h-64 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #ef4444, transparent 70%)" }} />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }} />

        <div className="relative p-8 text-center">
          {/* Pulsing bell */}
          <div className="relative mx-auto mb-6 w-32 h-32">
            <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: "radial-gradient(circle, #ef4444, transparent 65%)" }} />
            <div className="absolute inset-0 rounded-full animate-pulse opacity-40" style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)", animationDelay: "0.4s" }} />
            <div
              className="relative w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)",
                boxShadow: "0 15px 40px -5px rgba(239, 68, 68, 0.75)",
              }}
            >
              <svg width="70" height="70" viewBox="0 0 24 24" fill="white">
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
              </svg>
            </div>
          </div>

          <h2 className="text-3xl font-black text-white mb-2.5 tracking-tight" style={{ textShadow: "0 2px 15px rgba(239, 68, 68, 0.5)" }}>
            Allow Notifications
          </h2>
          <p className="text-orange-200 text-xs font-bold tracking-[0.2em] uppercase mb-5">
            Reddy Club Alerts
          </p>
          <p className="text-slate-200 text-sm mb-7 leading-relaxed">
            <b className="text-white">Result</b>, <b className="text-white">reminders</b> & <b className="text-white">winning tips</b> instantly milengi
          </p>

          <button
            onClick={allow}
            data-testid="allow-notif-btn"
            className="group relative w-full py-5 rounded-2xl font-black text-white text-xl tracking-wide transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] shadow-2xl overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)",
              boxShadow: "0 12px 35px -5px rgba(239, 68, 68, 0.65), 0 0 0 1px rgba(255,255,255,0.2) inset",
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <span className="relative flex items-center justify-center gap-3">
              🔔 ALLOW NOW
            </span>
          </button>

          <button
            onClick={dismiss}
            data-testid="allow-notif-skip"
            className="mt-4 text-slate-500 hover:text-slate-300 text-xs font-semibold tracking-widest uppercase transition-colors"
          >
            Not now
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.85) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
